<template>
  <div>
    <div class="d-flex align-center flex-wrap ga-3 mb-4">
      <div class="text-h6">Org Insights</div>
      <v-spacer />
      <v-select
        v-model="rangeDays"
        :items="rangeOptions"
        item-title="label"
        item-value="value"
        density="compact"
        variant="outlined"
        hide-details
        style="max-width: 200px"
        @update:modelValue="fetchUsage"
      />
    </div>

    <div class="text-caption text-medium-emphasis mb-4">
      How your team is using AI across features and projects (by token volume).
    </div>

    <div v-if="loading" class="d-flex justify-center pa-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else-if="rows.length === 0" class="text-center pa-8 text-medium-emphasis">
      <v-icon size="40" class="mb-2 text-disabled">mdi-chart-box-outline</v-icon>
      <div>No AI usage recorded for this organization in the selected range.</div>
    </div>

    <div v-else class="breakdown-grid">
      <v-card variant="outlined" class="pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-3">By feature</div>
        <div v-for="row in byUseCase" :key="row.key" class="bar-row">
          <div class="bar-label text-truncate">{{ row.label }}</div>
          <div class="bar-track">
            <div class="bar-fill bar-fill--alt" :style="{ width: barWidth(row.tokens, maxUseCaseTokens) }" />
          </div>
          <div class="bar-value">{{ formatTokens(row.tokens) }}</div>
        </div>
      </v-card>

      <v-card variant="outlined" class="pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-3">By project</div>
        <div v-for="row in byProject" :key="row.key" class="bar-row">
          <div class="bar-label text-truncate">{{ row.label }}</div>
          <div class="bar-track">
            <div class="bar-fill bar-fill--alt2" :style="{ width: barWidth(row.tokens, maxProjectTokens) }" />
          </div>
          <div class="bar-value">{{ formatTokens(row.tokens) }}</div>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useOrgUsage } from '@/composables/useOrgUsage'
import { formatTokens, barWidth } from '@/utils/usageFormat'
import { useCaseLabel } from '@/ai/useCases'

const props = defineProps({
  orgId: { type: String, required: true }
})

const { rangeDays, rangeOptions, rows, loading, projectNames, tokensGroupedBy, load, fetchUsage } = useOrgUsage(props.orgId)

onMounted(load)

const byUseCase = computed(() => tokensGroupedBy((r) => r.useCase, useCaseLabel))
const byProject = computed(() => tokensGroupedBy((r) => r.projectId, (id) => projectNames.value[id] || 'Unknown'))

const maxUseCaseTokens = computed(() => Math.max(...byUseCase.value.map((x) => x.tokens), 1))
const maxProjectTokens = computed(() => Math.max(...byProject.value.map((x) => x.tokens), 1))
</script>

<style scoped>
.breakdown-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
}
.bar-row {
  display: grid;
  grid-template-columns: 130px 1fr 64px;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.bar-label {
  font-size: 13px;
}
.bar-track {
  background: rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 4px;
  height: 10px;
  overflow: hidden;
}
.bar-fill {
  height: 100%;
  border-radius: 4px;
}
.bar-fill--alt {
  background: rgb(var(--v-theme-secondary));
}
.bar-fill--alt2 {
  background: rgb(var(--v-theme-info));
}
.bar-value {
  font-size: 13px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
</style>
